import React from 'react';
import { Card } from '@/components/ui/card';
import { ROLE_LABELS, SHIFT_META } from '@/lib/constants';
import { AlertTriangle, Info, XCircle, ShieldCheck } from 'lucide-react';

function Bar({ label, value }) {
  const color = value >= 90 ? '#10B981' : value >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-slate-500">{label}</span>
        <span className="font-mono font-semibold text-slate-700">{value}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

const SEV_ICON = { error: XCircle, warning: AlertTriangle, info: Info };
const SEV_COLOR = { error: 'text-red-600', warning: 'text-amber-600', info: 'text-slate-500' };

export function Legend() {
  return (
    <div className="flex flex-wrap gap-2" data-testid="shift-legend">
      {['R', 'P', 'M', 'C', 'SO', 'NE', 'X', 'D'].map((c) => {
        const m = SHIFT_META[c];
        return (
          <span key={c} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border" style={{ backgroundColor: m.bg, color: m.text, borderColor: m.border }}>
            <span className="font-bold">{m.short}</span> {m.label}
          </span>
        );
      })}
    </div>
  );
}

export default function StatsPanel({ stats }) {
  if (!stats) return null;
  const compliance = stats.compliance_pct;
  const cColor = compliance >= 90 ? 'bg-green-100 text-green-800 border-green-200' : compliance >= 60 ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-red-100 text-red-800 border-red-200';

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="stats-panel">
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">Dodržanie Zákonníka práce</h3>
        </div>
        <div className={`text-3xl font-bold font-heading mb-1 inline-flex px-3 py-1 rounded-md border ${cColor}`} data-testid="compliance-badge">
          {compliance}%
        </div>
        <div className="mt-4 space-y-3">
          <Bar label="Obsadenie zmien" value={stats.coverage_fill_pct} />
          <Bar label="Týždenný odpočinok (§93)" value={stats.rest_ok_pct} />
          <Bar label="Bez nadčasu (§85)" value={stats.overtime_ok_pct} />
        </div>
        <div className="text-xs text-slate-400 mt-3 font-mono">
          Obsadené {stats.filled_slots}/{stats.required_slots} slotov
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Odpracované hodiny</h3>
        <div className="overflow-y-auto max-h-64">
          <table className="w-full text-xs" data-testid="hours-table">
            <thead>
              <tr className="text-slate-400 text-left">
                <th className="pb-1 font-medium">Zamestnanec</th>
                <th className="pb-1 font-medium text-right">Hod.</th>
                <th className="pb-1 font-medium text-right">Víkend</th>
                <th className="pb-1 font-medium text-right" title="Najviac odpracovaných dní za sebou (max. séria bez voľna)">Dní po sebe</th>
              </tr>
            </thead>
            <tbody>
              {stats.per_employee.map((e) => {
                const ot = e.weeks.reduce((s, w) => s + w.overtime, 0);
                return (
                  <tr key={e.id} className={`border-t border-slate-100 ${e.is_virtual ? 'bg-amber-50' : ''}`}>
                    <td className="py-1.5">
                      <div className="font-medium text-slate-700 flex items-center gap-1.5">
                        {e.is_virtual && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />}{e.name}
                      </div>
                      <div className={`text-[10px] ${e.is_virtual ? 'text-amber-600' : 'text-slate-400'}`}>{e.is_virtual ? 'Navyše' : (ROLE_LABELS[e.role] || e.role)}</div>
                    </td>
                    <td className="py-1.5 text-right font-mono">
                      {e.total_hours}h{ot > 0 && <span className="text-amber-600"> (+{ot})</span>}
                    </td>
                    <td className="py-1.5 text-right font-mono">{e.weekend_days}</td>
                    <td className={`py-1.5 text-right font-mono ${e.max_streak > 10 ? 'text-red-600 font-bold' : ''}`}>{e.max_streak}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Upozornenia ({stats.warnings.length})</h3>
        <div className="overflow-y-auto max-h-64 space-y-2" data-testid="warnings-list">
          {stats.warnings.length === 0 && <div className="text-xs text-green-600">Žiadne porušenia. 👍</div>}
          {stats.warnings.map((w, i) => {
            const Icon = SEV_ICON[w.severity] || Info;
            return (
              <div key={i} className="flex gap-2 text-xs">
                <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${SEV_COLOR[w.severity]}`} />
                <span className="text-slate-600">{w.message}</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
